import { FC } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ChainTransportModelType, TransportModelType, TypeTransportModelType } from "../../../../../common/modelTypes";
import { col3, col6 } from "../../../../../common/utils";
import PieChartDistributionType from "../../../../../components/charts/graph/PieChartDistributionType";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import { AmbientalKpiWidget } from "../../../../../components/common/AmbientalKpiWidget";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import TableData from "../../../../../components/common/table/TableData";
import { EmissionsTransportsResultsDatumDTO } from "../../../../../datasources/api-client/data-contracts";
import { TransportPerspective } from "../../../../../state/preferences";
import { ThemeColors, TransportTypesColors } from "../../../../../theme/themeValues";
import EmissionChaiTransport from "../../components/EmissionChainTransport";
import EmissionsMap from "../../components/EmissionsMap";
import { dataCol, getColumns, getDataChart, getRows } from "./data";

type EmissionType = keyof Pick<EmissionsTransportsResultsDatumDTO, "co2Ttw" | "co2Wtw">;

interface Props {
	keyEmissions: string;
	transportEmissions: TransportModelType;
	emissionTypeTransport: TypeTransportModelType;
	emissionsChainTransport: ChainTransportModelType;
	selectedSource: string;
	onSourceChange: (value: string) => void;
	perspective?: TransportPerspective;
}

const Greenrouter: FC<Props> = ({
	transportEmissions,
	keyEmissions,
	emissionTypeTransport,
	emissionsChainTransport,
	selectedSource,
	onSourceChange,
	perspective,
}) => {
	const { t } = useTranslation(["result.transport", "common"]);

	const colors = [
		TransportTypesColors.LightRoad,
		TransportTypesColors.LightTrain,
		TransportTypesColors.LightShip,
		TransportTypesColors.LightAir,
	];

	const overrideFieldName = (old?: string) => {
		switch (old) {
			case "emissions":
				return keyEmissions;
			default:
				return old;
		}
	};

	const isWtw = keyEmissions === "co2Wtw";

	const marketBase: MarketBaseType = {
		emissions: isWtw
			? transportEmissions.data?.greenRouter?.current.marketBasedWtw
			: transportEmissions.data?.greenRouter?.current.marketBasedTtw,
		delta: isWtw
			? transportEmissions.data?.greenRouter?.current.marketBasedWtwDelta
			: transportEmissions.data?.greenRouter?.current.marketBasedTtwDelta,
		coverage: null,
		isLoading: transportEmissions.isLoading,
		onReload: transportEmissions.reload,
	};

	return (
		<Grid container spacing={2}>
			<Grid size={col3} minHeight={180}>
				<NumericValueWidget
					title={t("transport emissions")}
					delta={transportEmissions?.data?.greenRouter.delta?.[keyEmissions as EmissionType]}
					value={transportEmissions.data?.greenRouter.current?.[keyEmissions as EmissionType]?.value}
					measure={transportEmissions.data?.greenRouter.current?.[keyEmissions as EmissionType]?.measure}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</Grid>
			<Grid size={col3} minHeight={180}>
				<NumericValueWidget
					title={t("number of transports")}
					value={transportEmissions.data?.greenRouter?.current?.count}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</Grid>
			<Grid size={col3} minHeight={180}>
				<NumericValueWidget
					title={t("average weight for transport")}
					value={transportEmissions.data?.greenRouter?.current?.avgWeight.value}
					measure={transportEmissions.data?.greenRouter?.current?.weight.measure + "/" + t("transport")}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</Grid>
			<Grid size={col3} minHeight={180}>
				<NumericValueWidget
					title={t("average distance for transport")}
					value={transportEmissions.data?.greenRouter?.current?.avgDistance.value}
					measure={transportEmissions.data?.greenRouter?.current?.distance.measure}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</Grid>
			<Grid size={col6} minHeight={360}>
				<TableData
					columns={getColumns(
						dataCol,
						emissionTypeTransport.data ? emissionTypeTransport.data.total : {},
						t,
						overrideFieldName
					)}
					rows={emissionTypeTransport.data ? getRows(emissionTypeTransport.data, keyEmissions, t) : []}
					title={t("emissions by transport mode")}
					isLoading={emissionTypeTransport.isLoading}
					onReload={emissionTypeTransport.reload}
				/>
			</Grid>
			<Grid size={col6} minHeight={360}>
				<PieChartDistributionType
					totalEmissions={emissionTypeTransport.data?.total?.[keyEmissions as EmissionType]?.value || 0}
					keyEmissions={keyEmissions}
					title={t("distribution type")}
					isLoading={emissionTypeTransport.isLoading}
					onReload={emissionTypeTransport.reload}
					dataChart={emissionTypeTransport.data && getDataChart(emissionTypeTransport.data, keyEmissions, t)}
					colors={colors}
				/>
			</Grid>
			<Grid size={col6} minHeight={400}>
				<EmissionsMap
					title={t("emissions for")}
					color={ThemeColors.DarkPrimary}
					selectedSource={selectedSource}
					onSourceChange={onSourceChange}
					perspective={perspective}
				/>
			</Grid>
			<Grid size={col6}>
				<EmissionChaiTransport
					title={t("transport chain results")}
					data={emissionsChainTransport.data}
					isLoading={emissionsChainTransport.isLoading}
					onReload={emissionsChainTransport.reload}
				/>
			</Grid>

			<AmbientalKpiWidget
				bio={transportEmissions.data?.greenRouter.current.biogenic}
				pmx={transportEmissions.data?.greenRouter.current.pmx}
				loading={transportEmissions.isLoading}
				onReload={transportEmissions.reload}
			/>

			<MarketBaseWidget marketBase={marketBase} />
		</Grid>
	);
};

export default Greenrouter;
